import React, { useCallback, useEffect, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import ThunderstormRoundedIcon from "@mui/icons-material/ThunderstormRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { chatApi } from "../services/api";
import { openAuth } from "../utils/authBus";
import { useAuth } from "../auth/AuthContext";
import { formatTRY, formatChatTRY } from "../utils/money";
import ChatCaseRoll from "./ChatCaseRoll";

const TIP_PRESETS = [25, 50, 100, 250];
const RAIN_PRESETS = [10, 25, 50, 100];
const CASE_TOTAL_PRESETS = [500, 1000, 2500, 5000];
const CASE_SLOTS_PRESETS = [5, 10, 15, 20];

function timeOf(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

const GlobalChat = () => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [staff, setStaff] = useState(false);
  const canNuke = user?.role === "founder" || user?.role === "admin";
  const [tipFor, setTipFor] = useState(null);
  const [tipAmount, setTipAmount] = useState(50);
  const [rainOpen, setRainOpen] = useState(false);
  const [rainAmount, setRainAmount] = useState(25);
  const [rainRecipients, setRainRecipients] = useState(0);
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseTotal, setCaseTotal] = useState(1000);
  const [caseSlots, setCaseSlots] = useState(10);
  const [caseMap, setCaseMap] = useState({});
  const [caseFlash, setCaseFlash] = useState("");
  const [caseRoll, setCaseRoll] = useState(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const lastId = useRef(0);

  useEffect(() => {
    const toggle = () => setOpen((value) => !value);
    window.addEventListener("marsilya-open-chat", toggle);
    return () => window.removeEventListener("marsilya-open-chat", toggle);
  }, []);

  const pull = useCallback(() => {
    if (!user) return;
    chatApi
      .list(lastId.current)
      .then((data) => {
        setStaff(Boolean(data.staff));
        if (data.cases) setCaseMap(data.cases);
        const fresh = data.messages || [];
        if (!fresh.length) return;
        lastId.current = fresh[fresh.length - 1].id;
        setMessages((current) => {
          const merged = [...current, ...fresh];
          return merged.slice(-120);
        });
      })
      .catch(() => {});
  }, [user]);

  const pingPresence = useCallback(() => {
    if (!user) return;
    chatApi
      .presence(true)
      .then((data) => setRainRecipients(Number(data.recipients) || 0))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!open || !user) return undefined;
    pull();
    pingPresence();
    const pullTimer = setInterval(pull, 4000);
    const pingTimer = setInterval(pingPresence, 12000);
    return () => {
      clearInterval(pullTimer);
      clearInterval(pingTimer);
      chatApi.presence(false).catch(() => {});
    };
  }, [open, user, pull, pingPresence]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true);
    setError("");
    chatApi
      .send(value)
      .then((data) => {
        setText("");
        if (data.nuke) {
          setMessages(data.message ? [data.message] : []);
          lastId.current = data.message?.id || 0;
          return;
        }
        if (data.message) {
          setMessages((current) => [...current, data.message].slice(-120));
          lastId.current = Math.max(lastId.current, data.message.id || 0);
          return;
        }
        pull();
      })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false));
  };

  const dropCase = () => {
    if (busy || caseTotal < 100 || caseSlots < 3) return;
    setBusy(true);
    setError("");
    chatApi
      .createCase({ total: caseTotal, slots: caseSlots })
      .then(() => {
        setCaseOpen(false);
        pull();
        refresh().catch(() => {});
      })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false));
  };

  const finishCaseRoll = useCallback((payload) => {
    const data = payload?.data;
    const amount = Number(payload?.amount) || 0;
    setCaseRoll(null);
    setCaseFlash("");
    if (data?.case?.id) {
      setCaseMap((prev) => ({ ...prev, [data.case.id]: data.case }));
    }
    if (data?.roll) {
      setMessages((current) => [...current, data.roll].slice(-120));
      lastId.current = Math.max(lastId.current, data.roll.id || 0);
    }
    pull();
    refresh().catch(() => {});
    setBusy(false);
  }, [pull, refresh]);

  const openCase = (caseId) => {
    if (!caseId || busy || caseRoll) return;
    setBusy(true);
    setError("");
    chatApi
      .openCase(caseId)
      .then((data) => {
        setCaseRoll({ caseId, amount: Number(data.amount) || 0, data });
      })
      .catch((err) => {
        setError(err.message);
        setBusy(false);
      });
  };

  const sendRain = () => {
    if (busy || rainAmount < 5) return;
    setBusy(true);
    setError("");
    chatApi
      .rain({ amount: rainAmount })
      .then(() => {
        setRainOpen(false);
        pull();
        pingPresence();
        refresh().catch(() => {});
      })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false));
  };

  const sendTip = () => {
    if (!String(tipFor || "").trim() || busy) return;
    setBusy(true);
    setError("");
    chatApi
      .tip({ to: tipFor, amount: tipAmount })
      .then(() => {
        setTipFor(null);
        pull();
        refresh().catch(() => {});
      })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false));
  };

  const drop = (id) => {
    chatApi
      .remove(id)
      .then(() => setMessages((current) => current.filter((item) => String(item.id) !== String(id))))
      .catch(() => {});
  };

  if (!open) {
    return caseRoll ? (
      <ChatCaseRoll amount={caseRoll.amount} onDone={() => finishCaseRoll(caseRoll)} />
    ) : null;
  }

  return (
    <>
    <aside className="gc-panel" role="dialog" aria-label="Sohbet">
      <header className="gc-head">
        <div className="gc-title">
          <ForumRoundedIcon fontSize="small" />
          <div>
            <b>Sohbet</b>
            <small>Bahşiş · yağmur · kasa</small>
          </div>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Kapat">
          <CloseRoundedIcon fontSize="small" />
        </button>
      </header>

      {!user ? (
        <div className="gc-guest">
          <p>Sohbete katılmak için giriş yap.</p>
          <button type="button" onClick={() => openAuth("login")}>
            Giriş yap
          </button>
        </div>
      ) : (
        <>
          <div className="gc-list" ref={listRef}>
            {!messages.length ? <p className="gc-empty">Henüz mesaj yok. İlk yazan sen ol.</p> : null}
            {messages.map((item) => (
              <div
                key={item.id}
                className={`gc-msg${item.kind === "tip" ? " is-tip" : ""}${item.kind === "rain" ? " is-rain" : ""}${item.kind === "case" ? " is-case" : ""}${item.kind === "case_roll" ? " is-case-roll" : ""}${item.kind === "system" ? " is-system" : ""}`}
              >
                <div className="gc-msg-ava">
                  {item.avatar ? <img src={item.avatar} alt="" referrerPolicy="no-referrer" /> : <i>{String(item.username || "?").slice(0, 1).toUpperCase()}</i>}
                </div>
                <div className="gc-msg-body">
                  <div className="gc-msg-top">
                    <button
                      type="button"
                      className={`gc-user${item.role === "staff" ? " staff" : ""}`}
                      onClick={() => {
                        if (item.username === "Gizlenmiş" || String(item.userId) === String(user.id)) return;
                        setTipFor(item.username);
                      }}
                    >
                      {item.username}
                    </button>
                    {item.rank?.name ? <span className="gc-rank">{item.rank.name}</span> : null}
                    {item.username !== "Gizlenmiş" && String(item.userId) !== String(user.id) ? (
                      <button type="button" className="gc-tip-mini" onClick={() => setTipFor(item.username)} aria-label="Bahşiş ver">
                        <VolunteerActivismRoundedIcon sx={{ fontSize: 14 }} />
                      </button>
                    ) : null}
                    <time>{timeOf(item.createdAt)}</time>
                    {staff ? (
                      <button type="button" className="gc-drop" onClick={() => drop(item.id)} aria-label="Sil">
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                      </button>
                    ) : null}
                  </div>
                  <p>
                    {item.kind === "tip" ? <VolunteerActivismRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null}
                    {item.kind === "rain" ? <ThunderstormRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null}
                    {item.kind === "case" ? <RedeemRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null}
                    {item.kind === "case_roll" ? <RedeemRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null}
                    {item.text}
                  </p>
                  {item.kind === "case" && item.caseId ? (
                    (() => {
                      const cs = caseMap[item.caseId];
                      const left = cs?.left ?? item.slots;
                      const closed = cs?.status === "closed" || cs?.status === "expired" || left <= 0;
                      return (
                        <div className="gc-case-card">
                          <div className="gc-case-bar">
                            <span style={{ width: `${Math.max(0, ((item.slots - left) / item.slots) * 100)}%` }} />
                          </div>
                          <small>
                            {closed ? "Kasa kapandı" : `${left} açılım kaldı`} · {formatChatTRY(item.total)} kasa
                          </small>
                          {!closed && !cs?.iOpened && String(item.userId) !== String(user.id) && (cs?.canOpen ?? true) ? (
                            <button type="button" className="gc-case-open" disabled={busy} onClick={() => openCase(item.caseId)}>
                              Kasayı aç
                            </button>
                          ) : null}
                          {!closed && cs && !cs.canOpen && String(item.userId) === String(user.id) ? (
                            <small className="gc-case-note">Sen bıraktın — başkaları açsın</small>
                          ) : null}
                          {!closed && cs?.iOpened ? (
                            <small className="gc-case-note">Bu kasayı açtın</small>
                          ) : null}
                        </div>
                      );
                    })()
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {caseFlash ? <p className="gc-case-flash">{caseFlash}</p> : null}

          {caseOpen ? (
            <div className="gc-case-drop">
              <div className="gc-tip-head">
                <b>Kasa bırak</b>
                <button type="button" onClick={() => setCaseOpen(false)} aria-label="Vazgeç">
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
              <p className="gc-rain-note">
                <strong>{caseTotal.toLocaleString("tr-TR")} ₺</strong> kasa bırakılır. <strong>{caseSlots}</strong> kişi
                sırayla açar; sürpriz ödül veya boş çıkabilir.
              </p>
              <label className="gc-case-label">Kasa tutarı (₺)</label>
              <div className="gc-tip-row">
                {CASE_TOTAL_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={caseTotal === value ? "on" : ""}
                    onClick={() => setCaseTotal(value)}
                  >
                    {value} ₺
                  </button>
                ))}
              </div>
              <div className="gc-tip-go">
                <input
                  type="number"
                  min={100}
                  value={caseTotal}
                  onChange={(event) => setCaseTotal(Number(event.target.value) || 0)}
                  aria-label="Kasa tutarı"
                />
              </div>
              <label className="gc-case-label">Kaç kişi açacak?</label>
              <div className="gc-tip-row">
                {CASE_SLOTS_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={caseSlots === value ? "on" : ""}
                    onClick={() => setCaseSlots(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="gc-tip-go">
                <input
                  type="number"
                  min={3}
                  max={40}
                  value={caseSlots}
                  onChange={(event) => setCaseSlots(Number(event.target.value) || 0)}
                  aria-label="Açılım sayısı"
                />
                <button type="button" disabled={busy || caseTotal < 100 || caseSlots < 3} onClick={dropCase}>
                  Kasa bırak
                </button>
              </div>
              <small>Bakiyen: {formatTRY(user.balance || 0)}</small>
            </div>
          ) : null}

          {rainOpen ? (
            <div className="gc-rain">
              <div className="gc-tip-head">
                <b>Para yağmuru</b>
                <button type="button" onClick={() => setRainOpen(false)} aria-label="Vazgeç">
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
              <p className="gc-rain-note">
                Sohbette şu an <strong>{rainRecipients}</strong> kişi var (sen hariç). Seçtiğin tutar kişi başı dağıtılır.
              </p>
              <div className="gc-tip-row">
                {RAIN_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={rainAmount === value ? "on" : ""}
                    onClick={() => setRainAmount(value)}
                  >
                    {value} ₺
                  </button>
                ))}
              </div>
              <div className="gc-tip-go">
                <input
                  type="number"
                  min={5}
                  value={rainAmount}
                  onChange={(event) => setRainAmount(Number(event.target.value) || 0)}
                  aria-label="Kişi başı tutar"
                />
                <button
                  type="button"
                  disabled={busy || rainAmount < 5 || rainRecipients < 1}
                  onClick={sendRain}
                >
                  Yağmur at
                </button>
              </div>
              <small>
                Toplam: {formatTRY(rainAmount * Math.max(rainRecipients, 0))} · Bakiyen: {formatTRY(user.balance || 0)}
              </small>
            </div>
          ) : null}

          {tipFor !== null ? (
            <div className="gc-tip">
              <div className="gc-tip-head">
                <b>Bahşiş</b>
                <button type="button" onClick={() => setTipFor(null)} aria-label="Vazgeç">
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
              <input
                className="gc-tip-who"
                value={tipFor}
                placeholder="Kullanıcı adı"
                onChange={(event) => setTipFor(event.target.value)}
                aria-label="Bahşiş alacak kullanıcı"
              />
              <div className="gc-tip-row">
                {TIP_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={tipAmount === value ? "on" : ""}
                    onClick={() => setTipAmount(value)}
                  >
                    {value} ₺
                  </button>
                ))}
              </div>
              <div className="gc-tip-go">
                <input
                  type="number"
                  min={5}
                  value={tipAmount}
                  onChange={(event) => setTipAmount(Number(event.target.value) || 0)}
                  aria-label="Bahşiş tutarı"
                />
                <button type="button" disabled={busy || tipAmount < 5 || !String(tipFor || "").trim()} onClick={sendTip}>
                  Gönder
                </button>
              </div>
              <small>Bakiyen: {formatTRY(user.balance || 0)}</small>
            </div>
          ) : null}

          {error ? <p className="gc-error">{error}</p> : null}
          {canNuke ? <p className="gc-staff-hint">Kurucu/admin: tüm sohbeti silmek için <code>!nuke</code> yaz</p> : null}

          <form className="gc-form" onSubmit={send}>
            <input
              value={text}
              maxLength={240}
              placeholder="Mesaj yaz…"
              onChange={(event) => setText(event.target.value)}
            />
            <button
              type="button"
              className="gc-case-btn"
              onClick={() => {
                setCaseOpen(true);
                setRainOpen(false);
                setTipFor(null);
              }}
              aria-label="Kasa bırak"
            >
              <RedeemRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="gc-rain-open"
              onClick={() => {
                setRainOpen(true);
                setCaseOpen(false);
                setTipFor(null);
                pingPresence();
              }}
              aria-label="Para yağmuru"
            >
              <ThunderstormRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="gc-tip-open"
              onClick={() => {
                setTipFor(tipFor || "");
                setRainOpen(false);
                setCaseOpen(false);
              }}
              aria-label="Bahşiş ver"
            >
              <VolunteerActivismRoundedIcon fontSize="small" />
            </button>
            <button type="submit" disabled={busy || !text.trim()} aria-label="Gönder">
              <SendRoundedIcon fontSize="small" />
            </button>
          </form>
        </>
      )}
    </aside>
    {caseRoll ? (
      <ChatCaseRoll amount={caseRoll.amount} onDone={() => finishCaseRoll(caseRoll)} />
    ) : null}
    </>
  );
};

export default GlobalChat;
